import { formatEnumLabel } from '../common/helper';
import {
  InternalApiEntitySchemaField,
  InternalApiEntitySchemaFieldType,
  InternalApiPersonsPage,
  InternalApiSearchOperator,
  InternalApiTransactionsPage,
} from '../api-internal';
import { isChildField, getOrderedSchemaFieldsOfSearchResults } from '../schema/helper';

export const getSchemaFieldsOfSearchResults = (
  page: InternalApiPersonsPage | InternalApiTransactionsPage,
  schemaFields: InternalApiEntitySchemaField[]
) => {
  const fieldNameSet = new Set<string>();
  page.content.forEach((person) =>
    Object.keys(person.attributes || {}).forEach((fieldName) => {
      fieldNameSet.add(fieldName);
    })
  );
  const fieldNamesWithMissingSchema: string[] = [];
  const schemaFieldsOfSearchResults = Array.from(fieldNameSet)
    .map((fieldName) => {
      const schemaField = schemaFields.find((field) => field.fieldName === fieldName && !isChildField(field));
      if (!schemaField) {
        fieldNamesWithMissingSchema.push(fieldName);
      }
      return schemaField;
    })
    .filter((schemaField): schemaField is InternalApiEntitySchemaField => !!schemaField);

  return {
    schemaFieldsOfSearchResults: getOrderedSchemaFieldsOfSearchResults(schemaFieldsOfSearchResults),
    fieldNamesWithMissingSchema,
  };
};

export const isSortable = (schemaField: InternalApiEntitySchemaField) => {
  return [InternalApiEntitySchemaFieldType.DATETIME, InternalApiEntitySchemaFieldType.NUMBER].includes(
    schemaField.type
  );
};

interface OperatorOptionsProps {
  schemaFieldType: InternalApiEntitySchemaFieldType | undefined;
}

export const notPrefix = 'NOT_';

export const getOperatorOptions = ({ schemaFieldType }: OperatorOptionsProps) => {
  const generalOptions = [
    {
      key: InternalApiSearchOperator.IS_EXACTLY,
      label: formatEnumLabel(InternalApiSearchOperator.IS_EXACTLY),
      value: InternalApiSearchOperator.IS_EXACTLY,
    },
    {
      key: notPrefix + InternalApiSearchOperator.IS_EXACTLY,
      label: 'Is not exactly',
      value: InternalApiSearchOperator.IS_EXACTLY,
    },
    {
      key: InternalApiSearchOperator.EXISTS,
      label: formatEnumLabel(InternalApiSearchOperator.EXISTS),
      value: InternalApiSearchOperator.EXISTS,
    },
    {
      key: notPrefix + InternalApiSearchOperator.EXISTS,
      label: 'Does not exist',
      value: InternalApiSearchOperator.EXISTS,
    },
  ];
  switch (schemaFieldType) {
    case InternalApiEntitySchemaFieldType.TEXT:
      return [
        ...generalOptions,
        {
          key: InternalApiSearchOperator.CONTAINS,
          label: 'Contains',
          value: InternalApiSearchOperator.CONTAINS,
        },
        {
          key: notPrefix + InternalApiSearchOperator.CONTAINS,
          label: 'Does not contain',
          value: InternalApiSearchOperator.CONTAINS,
        },
        {
          key: InternalApiSearchOperator.CONTAINS_WORD,
          label: 'Contains word',
          value: InternalApiSearchOperator.CONTAINS_WORD,
        },
        {
          key: notPrefix + InternalApiSearchOperator.CONTAINS_WORD,
          label: 'Does not contain word',
          value: InternalApiSearchOperator.CONTAINS_WORD,
        },
      ];
    case InternalApiEntitySchemaFieldType.KEYWORD:
      return [
        ...generalOptions,
        {
          key: InternalApiSearchOperator.STARTS_WITH,
          label: 'Starts with',
          value: InternalApiSearchOperator.STARTS_WITH,
        },
        {
          key: notPrefix + InternalApiSearchOperator.STARTS_WITH,
          label: 'Does not start with',
          value: InternalApiSearchOperator.STARTS_WITH,
        },
        {
          key: InternalApiSearchOperator.ENDS_WITH,
          label: 'Ends with',
          value: InternalApiSearchOperator.ENDS_WITH,
        },
        {
          key: notPrefix + InternalApiSearchOperator.ENDS_WITH,
          label: 'Does not end with',
          value: InternalApiSearchOperator.ENDS_WITH,
        },
      ];
    case InternalApiEntitySchemaFieldType.NUMBER:
    case InternalApiEntitySchemaFieldType.DATETIME:
      return [
        ...generalOptions,
        {
          key: InternalApiSearchOperator.GT,
          label: 'Is greater than',
          value: InternalApiSearchOperator.GT,
        },
        {
          key: InternalApiSearchOperator.GTE,
          label: 'Is greater than or equal to',
          value: InternalApiSearchOperator.GTE,
        },
        {
          key: InternalApiSearchOperator.LT,
          label: 'Is less than',
          value: InternalApiSearchOperator.LT,
        },
        {
          key: InternalApiSearchOperator.LTE,
          label: 'Is less than or equal to',
          value: InternalApiSearchOperator.LTE,
        },
      ];
    case InternalApiEntitySchemaFieldType.ENUM:
      return generalOptions;
    case InternalApiEntitySchemaFieldType.OBJECT:
      throw Error('Search operators are not supported for the OBJECT Schema Field type');
    case undefined:
      return generalOptions;
  }
};
